/**
 * The canonical face-oval ring, and the outline read off one frame's landmarks.
 *
 * `FACE_OVAL_RING` is MediaPipe's own `FACE_LANDMARKS_FACE_OVAL` connector set, walked into ring order: it starts at the superior pole (landmark 10), runs down one side to the menton (landmark 152) at index 18, and back up the other side.
 *
 * The mirror pairing below is not an assumption. It was checked against the canonical face mesh shipped inside the vendored `face_landmarker.task`, where every pair `RING[k]` and `RING[36 - k]` has identical y and z and exactly opposite x, to zero residual; `test/canonical-oval.test.ts` re-checks it from the extracted fixture. What that proves is the model's topology — which indices are defined as mirrors — and nothing about any real face, whose predicted outline is never truly symmetric. That is what the symmetry gate is for.
 */
import type { FaceLandmarks } from '@optiflow/pd-vision';

/** MediaPipe's face-oval connector set in ring order. Index 0 is the superior pole, index 18 the menton. */
export const FACE_OVAL_RING = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
] as const;

/** Where the two poles sit in the ring. */
export const SUPERIOR_INDEX = 0;
export const MENTON_INDEX = 18;

/** The 17 mirror pairs, as ring positions: `[k, 36 - k]`. Validated against the canonical mesh. */
export const MIRROR_PAIRS: readonly (readonly [number, number])[] = Array.from(
  { length: 17 },
  (_, i) => [i + 1, 35 - i] as const,
);

export interface Point {
  x: number;
  y: number;
}

/** One frame's face outline in pixels, in ring order, y increasing downwards as the image does. */
export type Outline = readonly Point[];

/**
 * The outline for one detected face, in pixels.
 *
 * Null when any ring landmark is missing, because a partial ring cannot be reasoned about — a gap would silently become a straight line through the face.
 */
export function outlineFrom(face: FaceLandmarks, width: number, height: number): Outline | null {
  const points: Point[] = [];
  for (const index of FACE_OVAL_RING) {
    const p = face.points[index];
    if (!p) return null;
    points.push({ x: p.x * width, y: p.y * height });
  }
  return points;
}
